import React, { useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';

import { GridLegacy as Grid, InputAdornment, TextField } from '@mui/material';

import { usePropertySetupContext } from '../../PricingToolSessionContext';

import { StayInputOverrides } from './otaParity/types';

export type StayInputsFormValues = {
  nights: number;
  baseSellPriceAud: number;
  prvInclusionsAud: number;
};

const DEFAULT_VALUES: StayInputsFormValues = {
  nights: 3,
  baseSellPriceAud: 0,
  prvInclusionsAud: 0,
};

function toFormValues(overrides: StayInputOverrides): StayInputsFormValues {
  return {
    nights: overrides.nights,
    baseSellPriceAud: overrides.baseSellPriceAud,
    prvInclusionsAud: overrides.prvInclusionsAud,
  };
}

interface Props {
  initialValues?: StayInputOverrides | null;
  onChange: (values: StayInputOverrides) => void;
}

export default function StayInputsForm({ initialValues, onChange }: Props) {
  const { control, watch } = useForm<StayInputsFormValues>({
    defaultValues: initialValues ? toFormValues(initialValues) : DEFAULT_VALUES,
    mode: 'onChange',
  });
  const values = watch();
  const { values: propertySetup } = usePropertySetupContext();
  const currencyCode = propertySetup?.currency ?? 'AUD';
  const isNonAud = currencyCode !== 'AUD';
  const fxRate = isNonAud && propertySetup?.fxRate ? propertySetup.fxRate : 1;
  const prvInclusionsNative = Math.round(values.prvInclusionsAud * fxRate * 100) / 100;

  useEffect(() => {
    onChange({
      nights: values.nights,
      currencyCode,
      fxRateAudToNative: fxRate,
      baseSellPriceAud: values.baseSellPriceAud,
      prvInclusionsAud: values.prvInclusionsAud,
      prvInclusionsNative: isNonAud ? prvInclusionsNative : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.nights, values.baseSellPriceAud, currencyCode, fxRate, values.prvInclusionsAud, prvInclusionsNative]);

  return (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item xs={6} sm={3} md={2}>
        <Controller
          name="nights"
          control={control}
          rules={{ min: { value: 1, message: '1-21 nights' }, max: { value: 21, message: '1-21 nights' } }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              onChange={(e) => field.onChange(+e.target.value)}
              label="Nights"
              type="number"
              size="small"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              InputProps={{ inputProps: { min: 1, max: 21 } }}
            />
          )}
        />
      </Grid>
      <Grid item xs={6} sm={3} md={2}>
        <Controller
          name="baseSellPriceAud"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => field.onChange(+e.target.value)}
              label="Base sell price"
              type="number"
              size="small"
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">AUD</InputAdornment> }}
            />
          )}
        />
      </Grid>
      <Grid item xs={6} sm={3} md={2}>
        <TextField label="Currency" value={currencyCode} size="small" fullWidth InputProps={{ readOnly: true }} />
      </Grid>
      {isNonAud && (
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            label={`FX rate (AUD → ${currencyCode})`}
            value={fxRate}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </Grid>
      )}
      <Grid item xs={6} sm={3} md={2}>
        <Controller
          name="prvInclusionsAud"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => field.onChange(+e.target.value)}
              label="Inclusion PRV value"
              type="number"
              size="small"
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">AUD</InputAdornment> }}
            />
          )}
        />
      </Grid>
      {isNonAud && (
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            label="Inclusion PRV value (native)"
            value={prvInclusionsNative}
            size="small"
            fullWidth
            InputProps={{
              readOnly: true,
              startAdornment: <InputAdornment position="start">{currencyCode}</InputAdornment>,
            }}
          />
        </Grid>
      )}
    </Grid>
  );
}
